/**
 * Kitchen guide pages.
 *
 *  - Seasonal calendar: the month links become tabs — one month shown at a
 *    time, the current one first. Without JavaScript all twelve months stay on
 *    the page and the links simply jump to them.
 *  - Measures: the tables are the reference, but nobody wants to do the sums at
 *    the stove. Three converters (grams ⇄ cups and spoons for every product the
 *    kitchen data knows a weight for, oven scales, tin sizes) sit above them.
 *    They are printed hidden and only unhidden here, so a reader without
 *    JavaScript is never shown controls that would do nothing.
 */

import { formatQty } from './qty';

/**
 * Seasonal calendar tabs.
 */
function initSeasons() {
	const nav = document.querySelector( '.season-tabs' );
	if ( ! nav ) {
		return;
	}
	const links = [ ...nav.querySelectorAll( 'a[href^="#month-"]' ) ];
	const panels = links.map( ( a ) =>
		document.getElementById( a.hash.slice( 1 ) )
	);
	if ( panels.some( ( p ) => ! p ) ) {
		return;
	}

	const show = ( index ) => {
		links.forEach( ( a, i ) =>
			a.setAttribute( 'aria-current', String( i === index ) )
		);
		panels.forEach( ( p, i ) => {
			p.hidden = i !== index;
		} );
	};

	const fromHash = links.findIndex(
		( a ) => a.hash === window.location.hash
	);
	const now = links.findIndex( ( a ) => a.hasAttribute( 'data-now' ) );
	const first = fromHash >= 0 ? fromHash : Math.max( 0, now );
	show( first );
	nav.classList.add( 'is-tabs' );
	// Phones: the strip scrolls sideways — start with the chosen month in the middle.
	const active = links[ first ];
	nav.scrollLeft =
		active.offsetLeft -
		nav.offsetLeft -
		( nav.clientWidth - active.offsetWidth ) / 2;

	links.forEach( ( a, i ) => {
		a.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			show( i );
			window.history.replaceState( null, '', a.hash );
			// Keep the chosen month in view in the scrolling strip on phones.
			a.scrollIntoView( {
				block: 'nearest',
				inline: 'center',
				behavior: window.matchMedia(
					'(prefers-reduced-motion: reduce)'
				).matches
					? 'auto'
					: 'smooth',
			} );
		} );
	} );
}

/**
 * Grams the way a kitchen reads them: 5 g steps once they are worth having.
 *
 * @param {number} g Grams.
 * @return {number} Rounded grams.
 */
function roundGrams( g ) {
	if ( g >= 20 ) {
		return Math.round( g / 5 ) * 5;
	}
	if ( g >= 3 ) {
		return Math.round( g );
	}
	return Math.round( g * 2 ) / 2;
}

/**
 * Converters on the measures page.
 */
function initMeasures() {
	const root = document.querySelector( '[data-measures]' );
	if ( ! root ) {
		return;
	}
	const cfg = window.winonweb || {};
	const lang = cfg.lang || 'uk';
	const decimal = ( cfg.grammar || {} ).decimal || ',';
	let items = [];
	let t = {};
	try {
		items = JSON.parse( root.dataset.grams || '[]' );
		t = JSON.parse( root.dataset.i18n || '{}' );
	} catch ( e ) {
		return;
	}
	if ( ! items.length ) {
		return;
	}
	const cupMl = Number( root.dataset.cup ) || 250;

	// A <dl> of "what that comes to" rows, rebuilt on every change. It is not a
	// live region on purpose: the reader is typing, and announcing every
	// keystroke would talk over them.
	const fill = ( el, pairs ) => {
		el.replaceChildren(
			...pairs.flatMap( ( pair ) => {
				const dt = document.createElement( 'dt' );
				dt.textContent = pair[ 0 ];
				const dd = document.createElement( 'dd' );
				dd.textContent = pair[ 1 ];
				return [ dt, dd ];
			} )
		);
	};

	// Commas: <input type="number"> hands back an empty string for "1,5" in
	// some locales, so the raw value is parsed rather than trusted.
	const num = ( input ) => {
		const v = Number( String( input.value ).replace( ',', '.' ) );
		return Number.isFinite( v ) && v > 0 ? v : 0;
	};

	/* ---------------- Grams, cups and spoons ---------------- */

	const product = root.querySelector( '[data-convert-product]' );
	const amount = root.querySelector( '[data-convert-amount]' );
	const unit = root.querySelector( '[data-convert-unit]' );
	const weightOut = root.querySelector( '[data-convert-weight-out]' );
	const PER_ML = { cup: cupMl, tbsp: 15, tsp: 5, ml: 1 };

	const weight = () => {
		const item = items[ Number( product.value ) ] || items[ 0 ];
		const n = num( amount );
		if ( ! n ) {
			weightOut.replaceChildren();
			return;
		}
		const ml =
			'g' === unit.value ? n / item.m : n * ( PER_ML[ unit.value ] || 1 );
		// Below a twentieth there is nothing to measure, and "0" would read as
		// an answer rather than as "not worth a spoon".
		const spoons = ( v ) => ( v < 0.05 ? '—' : formatQty( v, lang ) );
		fill( weightOut, [
			[
				t.grams,
				formatQty( roundGrams( ml * item.m ), lang ) + ' ' + t.g,
			],
			[ t.ml, formatQty( Math.round( ml ), lang ) + ' ' + t.mlUnit ],
			[ t.cup, spoons( ml / cupMl ) ],
			[ t.tbsp, spoons( ml / 15 ) ],
			[ t.tsp, spoons( ml / 5 ) ],
		] );
	};

	[ product, amount, unit ].forEach( ( el ) => {
		el.addEventListener( 'input', weight );
		el.addEventListener( 'change', weight );
	} );

	/* ---------------- Oven ---------------- */

	const temp = root.querySelector( '[data-convert-temp]' );
	const scale = root.querySelector( '[data-convert-scale]' );
	const ovenOut = root.querySelector( '[data-convert-oven-out]' );
	const ovens = Array.isArray( t.ovens ) ? t.ovens : [];

	const oven = () => {
		const n = num( temp );
		if ( ! n ) {
			ovenOut.replaceChildren();
			return;
		}
		const c = 'c' === scale.value ? n : ( ( n - 32 ) * 5 ) / 9;
		const f = ( c * 9 ) / 5 + 32;
		const rows = [
			[ '°C', String( Math.round( c / 5 ) * 5 ) ],
			[ '°F', String( Math.round( f / 5 ) * 5 ) ],
			[ t.fan, Math.max( 0, Math.round( ( c - 20 ) / 5 ) * 5 ) + ' °C' ],
		];
		// Gas marks only cover the baking range; outside it the row would lie.
		const near = ovens.reduce(
			( best, row ) =>
				Math.abs( row[ 0 ] - c ) < Math.abs( best[ 0 ] - c )
					? row
					: best,
			ovens[ 0 ] || [ -999, 0, 0, '' ]
		);
		if ( Math.abs( near[ 0 ] - c ) <= 10 ) {
			rows.push( [ t.gas, String( near[ 2 ] ) ] );
			rows.push( [ t.heat, String( near[ 3 ] ) ] );
		}
		fill( ovenOut, rows );
	};

	[ temp, scale ].forEach( ( el ) => {
		el.addEventListener( 'input', oven );
		el.addEventListener( 'change', oven );
	} );

	/* ---------------- Tins ---------------- */

	const tinFrom = root.querySelector( '[data-convert-tin-from]' );
	const tinTo = root.querySelector( '[data-convert-tin-to]' );
	const tinOut = root.querySelector( '[data-convert-tin-out]' );

	const tin = () => {
		const from = Number( tinFrom.value );
		const to = Number( tinTo.value );
		if ( ! from || ! to ) {
			tinOut.replaceChildren();
			return;
		}
		const mult = to / from;
		if ( Math.abs( mult - 1 ) < 0.04 ) {
			// A sentence, not a value: it goes in a note row (empty term) so it
			// runs the full width instead of being squeezed into the number column.
			fill( tinOut, [ [ '', t.same ] ] );
			return;
		}
		fill( tinOut, [
			[
				t.scale,
				'×' +
					String( Math.round( mult * 100 ) / 100 ).replace(
						'.',
						decimal
					),
			],
			[ '', t.tinNote ],
		] );
	};

	[ tinFrom, tinTo ].forEach( ( el ) =>
		el.addEventListener( 'change', tin )
	);

	weight();
	oven();
	tin();
	root.hidden = false;
}

/**
 * Table of contents: mark the section the reader is looking at.
 */
function initToc() {
	const toc = document.querySelector( '.kitchen-guide__toc' );
	if ( ! toc || ! window.IntersectionObserver ) {
		return;
	}
	const links = new Map();
	toc.querySelectorAll( 'a[href^="#"]' ).forEach( ( a ) => {
		const section = document.getElementById( a.hash.slice( 1 ) );
		if ( section ) {
			links.set( section, a );
		}
	} );
	if ( ! links.size ) {
		return;
	}
	const seen = new Set();
	const observer = new window.IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					seen.add( entry.target );
				} else {
					seen.delete( entry.target );
				}
			} );
			// The topmost section still on screen is the one being read.
			let top = null;
			links.forEach( ( a, section ) => {
				if (
					seen.has( section ) &&
					( ! top || section.offsetTop < top.offsetTop )
				) {
					top = section;
				}
			} );
			links.forEach( ( a, section ) => {
				a.toggleAttribute( 'data-current', section === top );
			} );
		},
		{ rootMargin: '-20% 0px -60% 0px' }
	);
	links.forEach( ( a, section ) => observer.observe( section ) );
}

export default function initGuides() {
	initSeasons();
	initMeasures();
	initToc();
}
